<template>
	<div class="relative isolate overflow-visible w-full max-w-container mx-auto">
		<div class="lg:hidden flex flex-col items-start gap-2 min-h-36 px-4 md:px-10">
			<motion.div
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(10px)' }"
				:transition="{ duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
				class="text-primary text-5xl sm:text-7xl tracking-tight font-semibold will-change-transform mt-2 lg:mt-0"
			>
				{{ $t('residential.hero.title') }}
			</motion.div>
			<Motion
				as-child
				:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
				:initial="{ opacity: 0, transform: 'translateY(25px)' }"
				:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
				:in-view-options="{ once: true }"
				class="will-change-transform"
			>
				<UButton
					:label="$t('links.check-availability')"
					:to="localePath('/get-fiber')"
					size="lg"
					class="w-48 uppercase"
					variant="outline"
					color="primary"
				/>
			</Motion>
		</div>
		<UPageHero
			reverse
			orientation="horizontal"
			:description="$t('residential.hero.description')"
			:ui="{
				container: 'flex flex-col items-center lg:grid py-24 md:py-32 lg:py-8 xl:py-24 gap-16 md:gap-x-0 md:gap-y-24 relative isolate overflow-visible'
			}"
			class="-mt-8"
		>
			<template #title>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="hidden lg:block text-primary text-5xl sm:text-7xl tracking-tight font-semibold lg:whitespace-nowrap will-change-transform"
				>
					{{ $t('residential.hero.title') }}
				</motion.div>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
					class="block text-4xl sm:text-6xl text-pretty tracking-tight font-semibold text-highlighted will-change-transform"
				>
					{{ $t('residential.hero.subtitle') }}
				</motion.div>
			</template>
			<template #description>
				<motion.div
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 0.8, delay: 1.3 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					{{ $t('residential.hero.description') }}
				</motion.div>
			</template>
			<template #links>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(25px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					<SignupAddressChecker
						:tracking-location="trackingLocation"
						compact
						class="will-change-transform"
					/>
				</Motion>
			</template>

			<div class="max-w-[220px] sm:max-w-[260px] lg:max-w-[320px] xl:max-w-[417px] w-full lg:flex lg:items-end lg:justify-end lg:ml-auto mt-8 sm:mt-0 lg:mt-42 xl:mt-16">
				<SvgImage
					id="svg-image-residential-hero"
					provider="storyblok"
					fetchpriority="high"
					preload
					sizes="300px sm:355px lg:437px xl:570px"
					:image="{
						url: hero?.image?.filename,
						alt: hero?.image?.alt || 'Maverix Fiber-fast internet',
						offsetY: -150,
						offsetX: 0,
						height: 570,
						width: 417,
						densities: 'x1'
					}"
					:circles="[
						{
							cx: 78,
							cy: 66,
							r: 36,
							fill: '#f98c03'
						},
						{
							cx: 396,
							cy: 136,
							r: 18,
							fill: '#f98c03'
						}
					]"
					:icons="[
						{
							iconName: 'i-custom-dollar-bill',
							iconClass: 'text-primary',
							borderClass: 'border-2 border-[#f98c03]',
							cx: 36,
							cy: 28,
							r: 35
						},
						{
							iconName: 'i-custom-home',
							iconClass: 'text-primary -mt-3 ml-1',
							borderClass: 'border-2 border-[#f98c03]',
							cx: 346,
							cy: 54,
							r: 47.5
						},
						{
							iconName: 'i-custom-speedometer',
							iconClass: 'text-primary',
							borderClass: 'border-2 border-[#FF8A02]',
							cx: -8,
							cy: 238,
							r: 40.5
						}
					]"
					:viewbox="{
						width: 417,
						height: 417
					}"
					class="h-full w-full lg:min-h-[350px] pb-6 xl:pb-24 lg:pb-0 lg:mt-24 xl:mt-16 sm:mr-8 lg:mr-16 xl:mr-6 xl:-mb-24 shrink-0"
				/>
			</div>
		</UPageHero>
	</div>
</template>

<script setup lang="ts">
import {
	motion, Motion
} from 'motion-v'

import type { HeroV2 } from '@/types/storyblok/hero'

defineProps({
	hero: {
		type: Object as PropType<HeroV2>,
		required: true
	},
	trackingLocation: {
		type: String,
		required: true
	}
})

const localePath = useLocalePath()
</script>
